/**
 * The cast panel's routes (docs/SPEC.md §6.3, §25.1; P28-T12 built them,
 * P28-T14 reads them): every object the film needs, one row each, and what
 * the person can do to a row. Each write is a change of the one change
 * pipeline, so the film changes through the change events like any other;
 * the rows come again with the ad's `cast` event.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { changesKey } from "./changes";
import { type PricedAnswer, api, needsConfirm } from "./client";
import { ME_KEY } from "./me";

/** Where a row's object comes from (`backend/film/cast.py::ORIGINS`):
 *  `figure` is a person (a figure of Objects), `crowd` a scene's crowd
 *  (P29). */
export type CastOrigin =
  "objects" | "design" | "photo" | "standin" | "figure" | "crowd";

/** `ok`; `missing` (a stand-in Objects has no match for); `kept` (a stand-in
 *  the person chose to keep); `making` (a design is being made for it in the
 *  studio); `found` (an Object arrived for it: `found` is its ref). */
export type CastState = "ok" | "missing" | "kept" | "making" | "found";

/** A person who holds the row's object (P30): their cast member's name and
 *  the ids of the scenes they hold it in, in the film's order. */
export interface HeldBy {
  name: string;
  scenes: string[];
}

export interface MadeFor {
  ad_id: string;
  title: string;
}

/** One row of the cast panel (`backend/film/cast.py::Row`). */
export interface CastReplacement {
  id: string;
  n: number;
  state: string;
  error_code?: string | null;
  error_params?: Record<string, unknown> | null;
}

export interface CastRow {
  /** The cast member's id, or `<scene>.<ref>` for a scene's own object
   *  (`<scene>.<crowd id>` for a crowd). */
  key: string;
  tags?: string[];
  asset?: {
    kind: "shape" | "variant" | "figure";
    id: string;
    version: number;
  } | null;
  name: string;
  /** The Object's, the figure's or the photo's picture; null for a
   *  stand-in and a crowd (the page draws a frame of its first scene). */
  thumb_url: string | null;
  origin: CastOrigin;
  /** The ids of the scenes that place it, in the film's order. */
  scenes: string[];
  state: CastState;
  /** The Object offered for a stand-in (`objects:<id>@<v>`), when `found`. */
  found: string | null;
  replacement?: CastReplacement | null;
  /** What a stand-in stands for ("sofa"), else null. */
  thing: string | null;
  /** The ads the Object was made for (§6.4). */
  made_for: MadeFor[];
  /** The design being made for the row, when `making` (P28-T14). */
  design?: string | null;
  /** Who holds it, one entry per person (P30; empty when nobody does). */
  held_by?: HeldBy[];
}

export interface CastAnswer {
  rows: CastRow[];
}

/** A write's answer: the change submitted (null for **Not now**, and for a
 *  **Replace** with the Object the row already is, which says so in `say`:
 *  nothing to change, nothing to pay). */
export interface CastChange {
  change: CastReplacement | null;
  say?: string;
}

/** The sentence of a Replace that changed nothing (the row already is that
 *  Object), or null. */
export function alreadyThat(answer: unknown): string | null {
  const found = answer as Partial<CastChange> | null;
  return found && found.change === null && typeof found.say === "string"
    ? found.say
    : null;
}

export const castKey = (id: string) => ["ad-cast", id] as const;
const path = (id: string) => `/v1/ads/${encodeURIComponent(id)}/cast`;
/** A row's route: the key goes in as it is (one segment: ids are
 *  `^[a-z][a-z0-9_]*$`, a scene's own object `<scene>.<ref>`). */
const rowPath = (id: string, key: string, action: string) =>
  `${path(id)}/${encodeURIComponent(key)}/${action}`;

/** `GET /v1/ads/{id}/cast`. The socket's `cast` event writes new rows into
 *  the same query (`ad/useAdSocket.ts`). */
export function useCast(adId: string) {
  return useQuery({
    queryKey: castKey(adId),
    queryFn: () => api<CastAnswer>(path(adId)),
    refetchOnWindowFocus: false,
    refetchInterval: (query) =>
      query.state.data?.rows.some(
        (row) =>
          row.replacement &&
          ["queued", "working", "building", "needs_author"].includes(
            row.replacement.state,
          ),
      )
        ? 1000
        : false,
  });
}

/** After a write: the cast, the changes (the box shows the new one) and the
 *  balance (a photo's turn holds). */
function useAfterWrite(adId: string) {
  const queryClient = useQueryClient();
  return (answer: unknown) => {
    if (needsConfirm(answer)) return;
    void queryClient.invalidateQueries({ queryKey: castKey(adId) });
    void queryClient.invalidateQueries({ queryKey: changesKey(adId) });
    void queryClient.invalidateQueries({ queryKey: ME_KEY });
  };
}

/** What replaces a row: an Object, a design or a figure (`ref`, the brief's
 *  words: `objects:<id>@<v>`, `design:<id>@<v>`, `figure:<id>@<v>`), the
 *  person's photo (`upload_id`; a model's work, so it may answer the price
 *  question), or the figures a crowd is drawn from (`figures`,
 *  `figure:<id>@<v>` each; `[]` is every figure). */
export type Replacement =
  | { ref: string }
  | { upload_id: string; confirm?: boolean }
  | { figures: string[] };

/** `POST …/cast/{key}/replace`. */
export function useReplace(adId: string) {
  const after = useAfterWrite(adId);
  return useMutation({
    mutationFn: ({ key, ...body }: Replacement & { key: string }) =>
      api<CastChange | PricedAnswer>(rowPath(adId, key, "replace"), {
        method: "POST",
        body,
      }),
    onSuccess: after,
  });
}

/** `POST …/cast/{key}/keep`: **Use the stand-in**. */
export function useKeep(adId: string) {
  const after = useAfterWrite(adId);
  return useMutation({
    mutationFn: (key: string) =>
      api<CastChange>(rowPath(adId, key, "keep"), {
        method: "POST",
        body: {},
      }),
    onSuccess: after,
  });
}

/** `POST …/cast/{key}/found {use}`: **Yes** or **Not now**. */
export function useFound(adId: string) {
  const after = useAfterWrite(adId);
  return useMutation({
    mutationFn: ({ key, use }: { key: string; use: boolean }) =>
      api<CastChange>(rowPath(adId, key, "found"), {
        method: "POST",
        body: { use },
      }),
    onSuccess: after,
  });
}
